import Input from "@/v2-ui/input"
import Badge from "@/v2-backoffice-client/components/badge"
import Button from "@/v2-backoffice-client/components/button"
import {
  PRIVACY_JUSTIFICATION_LABEL,
  PRIVACY_JUSTIFICATION_MAX_LENGTH,
  PRIVACY_JUSTIFICATION_PLACEHOLDER,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.constants"

export type TenantPrivacyPolicyFooterProps = {
  /** Sources whose row differs from the server; 0 means nothing to save. */
  dirtyCount: number
  justification: string
  onJustificationChange(justification: string): void
  isSaving: boolean
  onSave(): void
  onCancel(): void
}

// The per-source table's footer, always present like every editor footer in
// the app: how many sources changed, the optional justification the change
// history keeps, and Cancel / Save. Everything is inert until a cell has
// been edited.
function TenantPrivacyPolicyFooter(props: TenantPrivacyPolicyFooterProps) {
  const {
    dirtyCount,
    justification,
    onJustificationChange,
    isSaving,
    onSave,
    onCancel,
  } = props

  const isDirty = dirtyCount > 0

  return (
    <div className="flex flex-wrap items-center gap-3">
      {isDirty && (
        <Badge size="sm" variant="warning">
          {dirtyCount} source{dirtyCount === 1 ? "" : "s"} changed
        </Badge>
      )}

      <Input
        className="max-w-96 flex-1"
        aria-label={PRIVACY_JUSTIFICATION_LABEL}
        maxLength={PRIVACY_JUSTIFICATION_MAX_LENGTH}
        placeholder={PRIVACY_JUSTIFICATION_PLACEHOLDER}
        disabled={!isDirty || isSaving}
        value={justification}
        onChange={(event) => {
          onJustificationChange(event.target.value)
        }}
      />

      <div className="flex-1" />

      <Button
        type="button"
        variant="outline"
        disabled={!isDirty || isSaving}
        data-feature-code="UI_BackofficeTenantPrivacy_Policy_Cancel"
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button
        type="button"
        disabled={!isDirty || isSaving}
        isLoading={isSaving}
        data-feature-code="UI_BackofficeTenantPrivacy_Policy_Save"
        onClick={onSave}
      >
        Save
      </Button>
    </div>
  )
}

export default TenantPrivacyPolicyFooter
